import { TConferenceProgress } from '../../spa/store/models/types';

const COUNTER_RE = /\s*\((\d+)\/(\d+)\)\s*$/;

// Key without the trailing counter, so "(1/3)" and "(2/3)" match.
const progressLogKey = (message: string): string =>
	message.replace(COUNTER_RE, '');

// Bump counter to max: "(2/3)" → "(3/3)".
const completeCounter = (message: string): string =>
	message.replace(COUNTER_RE, (_m, _cur, total) => ` (${total}/${total})`);

// Append message, collapsing repeats; on step change, bump prev counter to max.
export const mergeProgressLog = (prevLog: string[], message: string): string[] => {
	if (!message) return prevLog;
	const last = prevLog[prevLog.length - 1];
	if (last === undefined) return [...prevLog, message];
	if (last === message) return prevLog;
	// While cancelling, drop intermediate rollback progress — only a terminal line follows.
	if (last.startsWith('Cancelling')
		&& !message.startsWith('Cancelled')
		&& !message.startsWith('Failed')
		&& !message.startsWith('Done')) {
		return prevLog;
	}
	if (progressLogKey(last) === progressLogKey(message)) {
		return [...prevLog.slice(0, -1), message];
	}
	// A cancel interrupts the current step — keep its counter as-is instead of bumping to max.
	if (COUNTER_RE.test(last) && !message.startsWith('Cancel')) {
		return [...prevLog.slice(0, -1), completeCounter(last), message];
	}
	return [...prevLog, message];
};

// Snapshot + prev log → patch. Bumps progressStepStartedAt on each step change so the timer restarts.
// Timer baselines are in the client clock; the total baseline is anchored via the server-side
// elapsed (updatedAt - startedAt) — a diff of two server stamps, so clock skew cancels.
export const buildProgressPatch = (
	prevLog: string[] | undefined,
	progress: TConferenceProgress,
	prevStepStartedAt?: number,
	prevStartedAt?: number,
): {
	progress: TConferenceProgress;
	progressLog: string[];
	progressStartedAt: number;
	progressUpdatedAt: number;
	progressStepStartedAt: number;
} => {
	const progressLog = mergeProgressLog(prevLog || [], getConferenceProgressMessage(progress));
	const prevLast = prevLog && prevLog[prevLog.length - 1];
	const stepChanged = prevLast !== progressLog[progressLog.length - 1];
	const serverElapsed = Math.max(0, progress.updatedAt - progress.startedAt);
	const progressStartedAt = prevStartedAt ?? (Date.now() - serverElapsed);
	return {
		progress,
		progressLog,
		progressStartedAt,
		progressUpdatedAt: progressStartedAt + serverElapsed,
		progressStepStartedAt: stepChanged || prevStepStartedAt === undefined
			? Date.now()
			: prevStepStartedAt,
	}
};

// Terminal log lines end a run: Done / Failed / Cancelled.
export const isTerminalProgress = (message: string): boolean =>
	message.startsWith('Done') || message.startsWith('Failed') || message.startsWith('Cancelled');

// A run is still active while it has a log whose last line isn't terminal.
export const isProgressLogActive = (progressLog?: string[]): boolean => {
	const last = progressLog?.[progressLog.length - 1];
	return !!last && !isTerminalProgress(last);
};

// Maps a progress snapshot to a human-readable log line.
export const getConferenceProgressMessage = (p: TConferenceProgress): string => {
	const curr = Math.min(p.processedFiles + 1, p.totalFiles);
	switch (p.phase) {
		case 'WAITING_FILES':
			return `Waiting for recordings… (${p.foundFiles}/${p.expectedFiles})`;
		case 'REMUXING':
			return `Preparing audio… (${curr}/${p.totalFiles})`;
		case 'MERGING':
			return `Merging audio… (${curr}/${p.totalFiles})`;
		case 'UPLOADING':
			return 'Uploading…';
		case 'BUILDING_VIDEO':
			return 'Building video…';
		case 'TRANSCRIBING':
			return `Transcribing… (${curr}/${p.totalFiles})`;
		case 'MERGING_CAPTIONS':
			return 'Merging captions…';
		case 'SAVING_CAPTIONS':
			return p.captionsCount ? 'Saving captions…' : 'No captions';
		case 'GENERATING_INFO':
			return 'Generating summary…';
		case 'DONE': {
			// Reprocess produced nothing better, so the previous audio was kept.
			// Must start with "Done" so the tile/modal treat it as a terminal step and stop the timer.
			if (p.keptExisting) return `Done — kept the existing audio (the new run was no better, ${p.foundFiles}/${p.expectedFiles} files found)`;
			if (p.complete) return 'Done';
			const missed = p.expectedFiles - p.foundFiles;
			// No more files will arrive, so finishing without them is expected, not a failure.
			if (p.filesFinal) return `Done. ${missed} file${missed === 1 ? '' : 's'} missing`;
			// Finished but recordings still expected — treat as failure.
			return `Failed: some recordings are missing (${missed}/${p.expectedFiles})`;
		}
		case 'FAILED':
			return `Failed${p.error ? ': ' + p.error : ''}`;
		case 'CANCELLED':
			return 'Cancelled';
		default:
			return '';
	}
};
